import { useQuery } from "@tanstack/react-query";
import React from "react";

import { INSIGHTS_BACKEND_URL, NEST_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { useModuleEndpoint } from "@/shared/hooks/useModuleEndpoint";
import { axiosApi } from "@/Utils";

type FinancialReviewROI = {
  rois: {
    roi: number;
    total_spend: number;
    total_revenue: number;
    total_sales: number;
    units: {
      total: number;
    };
    brand: {
      spend: number;
      revenue: number;
      roi: number;
    };
    category: {
      spend: number;
      revenue: number;
      roi: number;
    };
    product: {
      spend: number;
      revenue: number;
      roi: number;
    };
  };
  brandBreakdown: {
    id: string;
    brand: string;
    total_spend: number;
    total_revenue: number;
    roi: number;
  }[];
  includeBusinessUnit: boolean;
};

// Define the API function for new endpoint
export const getFinancialReviewV2 = async ({
  startDate,
  endDate,
  brand,
  category,
  product,
  geographies,
  businessUnitToggle,
}: {
  startDate?: string | null;
  endDate?: string | null;
  brand: string;
  category: string;
  product: string;
  geographies?: string[];
  businessUnitToggle?: "aggregate" | "split";
}): Promise<FinancialReviewROI> => {
  const params = new URLSearchParams();

  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  params.append("brand", brand);
  params.append("category", category);
  params.append("product", product);

  if (businessUnitToggle) params.append("businessUnitToggle", businessUnitToggle);

  if (geographies?.length) {
    geographies.forEach((geo) => params.append("geographies", geo));
  }

  return axiosApi.get(`/api/v2/financial-review/mroi`, {
    baseURL: INSIGHTS_BACKEND_URL,
    params: {
      brand,
      category,
      product,
      startDate,
      endDate,
      geographies,
      businessUnitToggle,
    },
  });
};

// Define the API function for old endpoint
const _getFinancialReviewNest = async ({
  startDate,
  endDate,
  brand,
  category,
  product,
  geographies,
  businessUnitToggle,
}: {
  startDate?: string | null;
  endDate?: string | null;
  brand: string;
  category: string;
  product: string;
  geographies?: string[];
  businessUnitToggle?: "aggregate" | "split";
}): Promise<FinancialReviewROI> => {
  const params = new URLSearchParams();

  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  params.append("brand", brand);
  params.append("category", category);
  params.append("product", product);

  if (businessUnitToggle) params.append("businessUnitToggle", businessUnitToggle);

  if (geographies?.length) {
    geographies.forEach((geo) => params.append("geographies", geo));
  }

  return axiosApi.get(`/api/financial-review/mroi?${params.toString()}`, {
    baseURL: NEST_BACKEND_URL,
  });
};

// Create the hook
export const useFinancialReview = ({
  startDate,
  endDate,
  brand,
  category,
  product,
  geographies,
  businessUnitToggle = "aggregate",
  config,
}: {
  startDate?: string | null;
  endDate?: string | null;
  brand: string;
  category: string;
  product: string;
  geographies?: string[];
  businessUnitToggle?: "aggregate" | "split";
  config?: QueryConfig<typeof getFinancialReviewV2 | typeof _getFinancialReviewNest>;
}) => {
  const endpointType = useModuleEndpoint("financial-review");

  const queryKey = React.useMemo(
    () => [
      "financial-review",
      brand,
      category,
      product,
      startDate,
      endDate,
      geographies,
      businessUnitToggle,
      endpointType,
    ],
    [brand, category, product, startDate, endDate, geographies, businessUnitToggle, endpointType]
  );

  const queryFn = React.useCallback(() => {
    if (endpointType === "new") {
      return _getFinancialReviewNest({
        brand,
        category,
        product,
        startDate,
        endDate,
        geographies,
        businessUnitToggle,
      });
    } else {
      return getFinancialReviewV2({
        brand,
        category,
        product,
        startDate,
        endDate,
        geographies,
        businessUnitToggle,
      });
    }
  }, [brand, category, product, startDate, endDate, geographies, businessUnitToggle, endpointType]);

  return useQuery({
    ...config,
    queryKey,
    queryFn,
  });
};
